import { useState } from 'react';
import { css } from '@emotion/react';
import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { PhoneTree } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Text, useFormField, useModalControl } from '@frontend/design-system';
import { PhoneTreeDropdown } from '../../../../common/phone-tree-dropdown/phone-tree-dropdown';
import {
  extractSubGraph,
  getCanvasPhoneTrees,
  isPhoneHoursDescendant,
  isWeaveAssistantDescendant,
  sanitizeNodesForDuplicatePhoneTree,
  CanvasPhoneTree,
} from '../../../helpers/graph/node-utils';
import { useCallRouteGraphsShallowStore } from '../../call-route-store';
import { Step } from '../atoms/step';

type PendingDuplicate = {
  phoneTreeName: string;
  descendantNodes?: CallRouteV1.Types.Node[];
  descendantEdges?: Edge[];
  droppedRepeatNodes: ReturnType<typeof sanitizeNodesForDuplicatePhoneTree>['droppedRepeatNodes'];
  droppedPhoneHoursNodes: ReturnType<typeof sanitizeNodesForDuplicatePhoneTree>['droppedPhoneHoursNodes'];
  droppedWeaveAssistantNodes: ReturnType<typeof sanitizeNodesForDuplicatePhoneTree>['droppedWeaveAssistantNodes'];
};

export interface PhoneTreeDuplicateStepProps {
  currentCallRouteId?: string;
  callRouteLocationId?: string;
  callRouteName?: string;
  processingNodeId?: string;
  onDoneClick: (args: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }) => void;
}

export const PhoneTreeDuplicateStep = ({
  onDoneClick,
  currentCallRouteId,
  callRouteLocationId,
  callRouteName,
  processingNodeId,
}: PhoneTreeDuplicateStepProps) => {
  const { t } = useTranslation('phone');
  const {
    getTempEditingNodesByCallRouteId,
    getTempEditingEdgesByCallRouteId,
    getNodesByCallRouteId,
    getEdgesByCallRouteId,
  } = useCallRouteGraphsShallowStore(
    'getTempEditingNodesByCallRouteId',
    'getTempEditingEdgesByCallRouteId',
    'getNodesByCallRouteId',
    'getEdgesByCallRouteId'
  );
  const [isDuplicating, setIsDuplicating] = useState(false);
  const { modalProps: repeatWarningModalProps, triggerProps: repeatWarningTriggerProps } = useModalControl();
  const [pendingDuplicate, setPendingDuplicate] = useState<PendingDuplicate | null>(null);
  const [selectedPhoneTree, setSelectedPhoneTree] = useState<
    CanvasPhoneTree | (PhoneTree & { onCanvas: false }) | undefined
  >();

  const canvasNodes =
    getTempEditingNodesByCallRouteId(currentCallRouteId as string) ||
    getNodesByCallRouteId(currentCallRouteId as string) ||
    [];
  const canvasEdges =
    getTempEditingEdgesByCallRouteId(currentCallRouteId as string) ||
    getEdgesByCallRouteId(currentCallRouteId as string) ||
    [];

  const phoneTreesInCallRoute = currentCallRouteId ? getCanvasPhoneTrees(canvasNodes, canvasEdges) : [];

  const phoneTreeProps = useFormField({
    type: 'dropdown',
    required: true,
    value: '',
  });

  const fetchCallRoute = CallRouteV1.Queries.useFetchRead();

  const handleDoneClick = async () => {
    if (!phoneTreeProps.value || isDuplicating || !selectedPhoneTree) return;

    setIsDuplicating(true);

    const phoneTreeName = selectedPhoneTree?.name || 'Duplicated Phone Tree';

    try {
      let extracted: ReturnType<typeof extractSubGraph>;
      if (selectedPhoneTree?.onCanvas) {
        // onCanvas → identifier is node.id
        if (!currentCallRouteId) throw new Error('No call route ID');

        extracted = extractSubGraph(canvasNodes, canvasEdges, selectedPhoneTree.phoneTreeNodeId, 'byId');
      } else {
        // saved tree → identifier is primitiveId
        const callRouteId = selectedPhoneTree?.callRouteData?.id;
        const callRouteData = await fetchCallRoute({
          request: { callRouteId: callRouteId ?? '' },
          options: { staleTime: 0 },
        });

        extracted = extractSubGraph(
          callRouteData.nodes as CallRouteV1.Types.Node[],
          callRouteData.edges,
          selectedPhoneTree.id,
          'byPrimitiveId'
        );
      }

      if (!extracted) {
        throw new Error('Failed to extract phone tree data');
      }

      // Determine if the target node is already a descendant of a phone hours or weave assistant node.
      // If so, we must drop those node types (and their descendants) from the duplicated subgraph to avoid
      // nesting the same node type within itself.
      const dropPhoneHoursSubtrees = processingNodeId
        ? isPhoneHoursDescendant(processingNodeId, canvasNodes, canvasEdges)
        : false;
      const dropWeaveAssistantSubtrees = processingNodeId
        ? isWeaveAssistantDescendant(processingNodeId, canvasNodes, canvasEdges)
        : false;

      const { sanitized, droppedRepeatNodes, droppedPhoneHoursNodes, droppedWeaveAssistantNodes } =
        sanitizeNodesForDuplicatePhoneTree(
          [extracted.rootNode, ...extracted.descendantNodes],
          extracted.descendantEdges,
          {
            dropPhoneHoursSubtrees,
            dropWeaveAssistantSubtrees,
          }
        );

      const hasDroppedNodes =
        droppedRepeatNodes.length > 0 || droppedPhoneHoursNodes.length > 0 || droppedWeaveAssistantNodes.length > 0;

      if (hasDroppedNodes) {
        // There are nodes that will be dropped; show a warning before proceeding.
        setPendingDuplicate({
          phoneTreeName,
          descendantNodes: sanitized.nodes,
          descendantEdges: sanitized.edges,
          droppedRepeatNodes,
          droppedPhoneHoursNodes,
          droppedWeaveAssistantNodes,
        });
        repeatWarningTriggerProps.onClick();
      } else {
        // Normal case: no problematic nodes → continue as before.
        onDoneClick({ nodes: sanitized.nodes, edges: sanitized.edges });
      }
    } catch (error) {
      console.error('Failed to fetch phone tree details:', error);
      alert(t('An error occurred while duplicating the phone tree.'));
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <Step
      title={t('Duplicate Phone Tree')}
      primaryButtonLabel={isDuplicating ? t('Duplicating...') : t('Duplicate')}
      primaryButtonDisabled={!phoneTreeProps.value || isDuplicating}
      onPrimaryButtonClick={handleDoneClick}
    >
      <PhoneTreeDropdown
        dropdownProps={phoneTreeProps}
        phoneTreesInCallRoute={phoneTreesInCallRoute}
        currentCallRouteId={currentCallRouteId}
        currentCallRouteLocationId={callRouteLocationId}
        currentCallRouteName={callRouteName}
        prioritizedLocationId={callRouteLocationId}
        shouldFilterOutNotInCallRoute={true}
        onSelect={setSelectedPhoneTree}
        css={css`
          width: 320px;
        `}
      />

      {/* Confirmation modal when we are about to drop invalid steps */}
      {pendingDuplicate && (
        <ConfirmationModal
          {...repeatWarningModalProps}
          maxWidth={600}
          title={t('Remove Invalid Steps to Duplicate?')}
          message={
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(3)};
              `}
            >
              <Text>{t('Some steps are no longer valid and will not be included in the duplication.')}</Text>
              <Text>{t('Do you want to continue?')}</Text>
            </div>
          }
          cancelLabel={t('No, Cancel')}
          confirmLabel={t('Yes, Duplicate')}
          destructive={false}
          onCancel={() => {
            repeatWarningModalProps.onClose();
            setPendingDuplicate(null);
          }}
          onConfirm={() => {
            if (pendingDuplicate) {
              onDoneClick({
                nodes: pendingDuplicate.descendantNodes || [],
                edges: pendingDuplicate.descendantEdges || [],
              });
            }
            repeatWarningModalProps.onClose();
            setPendingDuplicate(null);
          }}
        />
      )}
    </Step>
  );
};
